import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';

// Brand voice (admin-api routes/brand-voice.ts): the acting brand's persona +
// per-use formats, and its reference images. n8n reads both through
// get_brand_prompt(brand, format).

export interface PromptVersion {
  prompt_key: string;
  version: number;
  description: string | null;
  body: string;
  tokens: Record<string, unknown>;
  is_active: boolean;
  created_by: string | null;
  created_at: string;
}

export interface PromptGroup {
  promptKey: string;
  description: string | null;
  activeVersion: number | null;
  versions: PromptVersion[];
}

export interface AssetSlot {
  key: string;
  label: string;
  hint: string;
  current: { asset_key: string; url: string; content_type: string | null; updated_by: string | null; updated_at: string } | null;
}

export interface BrandAssets {
  allowedTypes: string[];
  maxBytes: number;
  assets: AssetSlot[];
}

const PROMPTS_KEY = ['brand-settings', 'prompts'];
const ASSETS_KEY = ['brand-settings', 'assets'];

export function useBrandPrompts() {
  return useQuery({ queryKey: PROMPTS_KEY, queryFn: () => apiClient<{ prompts: PromptGroup[] }>('/brand-settings/prompts') });
}

export interface SavePromptInput {
  promptKey: string;
  body: string;
  tokens: Record<string, unknown>;
  description?: string | null;
}

/** Saves a new version and makes it live. */
export function useSavePrompt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ promptKey, ...payload }: SavePromptInput) =>
      apiClient<{ prompt: PromptVersion }>(`/brand-settings/prompts/${encodeURIComponent(promptKey)}`, {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PROMPTS_KEY }),
  });
}

/** Makes an older version live again. */
export function useActivatePrompt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ promptKey, version }: { promptKey: string; version: number }) =>
      apiClient<{ prompt: PromptVersion }>(`/brand-settings/prompts/${encodeURIComponent(promptKey)}/versions/${version}/activate`, {
        method: 'POST',
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PROMPTS_KEY }),
  });
}

export function useBrandAssets() {
  return useQuery({ queryKey: ASSETS_KEY, queryFn: () => apiClient<BrandAssets>('/brand-settings/assets') });
}

/** Uploads the raw file; the server checks type, size and content. */
export function useUploadAsset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ key, file }: { key: string; file: File }) =>
      apiClient<{ asset: AssetSlot['current'] }>(`/brand-settings/assets/${encodeURIComponent(key)}`, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ASSETS_KEY }),
  });
}
